import React from "react";
import { CheckCircle2, Circle } from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  WarningBox,
  InfoBox,
} from "@/tutorial/components/TutorialComponents";
import { cn } from "@/lib/utils";

const PRECONDITIONS = [
  { id: "budget", label: "L'analyse budgétaire est complète", done: true },
  { id: "legal", label: "L'analyse juridique est générée", done: true },
  { id: "corpus", label: "Au moins 1 texte légal a été uploadé", done: true },
  { id: "ack", label: "L'avertissement légal a été confirmé", done: false },
];

const ANNOT = [
  { n: 1, t: "En-tête institutionnel + bandeau pédagogique non-masquable." },
  { n: 2, t: "Devise vitaliste : « Au nom du peuple et de sa vie »." },
  { n: 3, t: "Exposé des faits : montant + traduction en jours/années de vie." },
  { n: 4, t: "Qualification : article applicable + niveau de gravité vitaliste." },
  { n: 5, t: "Dispositif : sanctions proposées (pénale, civile, administrative, vitaliste)." },
];

export default function Chapter7() {
  return (
    <ChapterShell id={7}>
      {/* --- AVERTISSEMENT OBLIGATOIRE EN PREMIER --- */}
      <WarningBox
        title="Simulation pédagogique uniquement"
        data-testid="chapter-7-disclaimer"
      >
        Ce jugement est un outil d'illustration académique basé sur la{" "}
        <em>Nouvelle Théorie des Finances Publiques</em> (Ahmed ELY Mustapha).
        Il n'a <strong>aucune valeur juridictionnelle</strong> et ne remplace
        pas l'avis d'un juriste qualifié.
      </WarningBox>

      <Section
        id="section-7-1"
        eyebrow="7.1"
        title="Conditions pour activer la simulation"
      >
        <p>
          Le bouton <em>Générer le jugement</em> reste verrouillé tant que les
          quatre conditions ci-dessous ne sont pas remplies.
        </p>
        <ul className="space-y-2" data-testid="chapter-7-checklist">
          {PRECONDITIONS.map((c) => {
            const Icon = c.done ? CheckCircle2 : Circle;
            return (
              <li
                key={c.id}
                className={cn(
                  "flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5",
                  c.done ? "opacity-100" : "opacity-70"
                )}
                data-testid={`chapter-7-condition-${c.id}`}
              >
                <Icon
                  className={cn(
                    "h-5 w-5 shrink-0",
                    c.done ? "text-emerald-500" : "text-muted-foreground"
                  )}
                />
                <span className="text-sm">{c.label}</span>
              </li>
            );
          })}
        </ul>
        <InfoBox title="Déverrouillage automatique">
          Le bouton se déverrouille uniquement quand les <strong>4 conditions</strong>{" "}
          sont vérifiées. Cette protection garantit qu'aucun jugement
          pédagogique ne soit produit sur des bases incomplètes.
        </InfoBox>
      </Section>

      <Section id="section-7-2" eyebrow="7.2" title="Lire le jugement simulé">
        <p>
          Voici une maquette du document généré, annotée pour en clarifier la
          structure.
        </p>

        <figure
          className="relative overflow-hidden rounded-xl border-2 border-brand-blue/30 bg-[#fdfaf1] p-5 font-judicial text-foreground shadow-md sm:p-8"
          data-testid="chapter-7-mockup"
        >
          {/* Annotation pins */}
          {[
            { left: "5%", top: "9%" },
            { left: "5%", top: "23%" },
            { left: "5%", top: "44%" },
            { left: "5%", top: "67%" },
            { left: "5%", top: "85%" },
          ].map((pos, i) => (
            <span
              key={i}
              aria-hidden="true"
              className="absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-gold text-xs font-black text-background shadow-md ring-2 ring-background"
              style={pos}
            >
              {i + 1}
            </span>
          ))}

          <div className="border-b-2 border-brand-blue/30 pb-3 text-center">
            <div className="text-[11px] font-bold uppercase tracking-widest text-brand-blue">
              Tribunal fictif vitaliste des finances publiques
            </div>
            <div className="mt-1 text-xs italic text-muted-foreground">
              VITAE-PUBLICA™ — document strictement pédagogique
            </div>
          </div>
          <div className="mt-4 text-center text-base font-bold text-brand-blue sm:text-lg">
            « Au nom du peuple et de sa vie »
          </div>

          <div className="mt-5 space-y-3 text-sm leading-relaxed">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-widest text-brand-gold">
                Exposé des faits
              </span>
              <p className="mt-1">
                Un montant de <strong>50 000 UM</strong> a été détourné des
                ressources publiques, représentant <strong>3 000 jours de vie</strong>{" "}
                des contribuables, soit environ <strong>8,2 années
                d'existence humaine</strong> confisquées illégalement.
              </p>
            </div>

            <div>
              <span className="text-[11px] font-bold uppercase tracking-widest text-brand-gold">
                Qualification
              </span>
              <p className="mt-1">
                Détournement de fonds publics — article 163 du Code pénal —
                gravité vitaliste : <strong>Niveau 3</strong>.
              </p>
            </div>

            <div>
              <span className="text-[11px] font-bold uppercase tracking-widest text-brand-gold">
                Dispositif (illustratif)
              </span>
              <ul className="mt-1 ml-4 list-disc space-y-1">
                <li>Restitution intégrale + intérêts au taux légal</li>
                <li>Peine d'emprisonnement proportionnelle au niveau 3</li>
                <li>Inéligibilité aux charges publiques</li>
                <li>Mesure vitaliste : confiscation totale + travail d'intérêt général proportionné aux années-vie soustraites</li>
              </ul>
            </div>
          </div>
        </figure>

        <ol
          className="space-y-2 rounded-lg border border-border bg-card p-4 text-sm"
          data-testid="chapter-7-mockup-legend"
        >
          {ANNOT.map((a) => (
            <li key={a.n} className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-gold text-[10px] font-bold text-background">
                {a.n}
              </span>
              <span>{a.t}</span>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="section-7-3" eyebrow="7.3" title="Interpréter le dispositif">
        <p>
          Le dispositif articule quatre branches : pénale, civile,
          administrative et <em>vitaliste</em>. La branche vitaliste est la
          contribution propre de la théorie : elle proportionne la sanction
          à la <strong>valeur-vie</strong> détournée et non plus uniquement au
          montant monétaire.
        </p>
        <InfoBox title="Lecture proportionnelle">
          Les sanctions sont proportionnelles à la <strong>valeur-vie</strong>{" "}
          détournée, pas seulement au montant monétaire. Deux infractions de
          même montant ne produisent pas la même peine si elles s'appliquent à
          des contribuables aux salaires journaliers très différents.
        </InfoBox>
        <WarningBox title="Rappel pédagogique">
          Aucun nom de personne, d'administration ou d'institution n'apparaît
          dans le jugement. La théorie vitaliste raisonne par catégories
          (« les ordonnateurs », « les décideurs budgétaires »).
        </WarningBox>
      </Section>
    </ChapterShell>
  );
}
